import { useEffect, useRef, type RefObject } from 'react';
import jsQR from 'jsqr';

/**
 * Декодирование QR из видеопотока (FR-MGR-01).
 *
 * Кадр уменьшается до 480 px по ширине и разбирается ~11 раз в секунду:
 * декодирование занимает единицы миллисекунд, поэтому распознавание укладывается
 * в требование «не более 1 с» (ТЗ §4.1), а поток — в «не менее 20 сканов
 * в минуту с одного устройства».
 */

export interface QrScannerOptions {
  videoRef: RefObject<HTMLVideoElement>;
  enabled: boolean;
  /** Интервал между разборами кадра, мс. */
  intervalMs?: number;
  /** Максимальная ширина кадра для разбора, px. */
  maxWidth?: number;
  onResult: (text: string) => void;
  /** Длительность разбора последнего кадра, мс — для контроля требования ТЗ §4.1. */
  onDecodeMs?: (milliseconds: number) => void;
}

export function useQrScanner({
  videoRef,
  enabled,
  intervalMs = 90,
  maxWidth = 480,
  onResult,
  onDecodeMs,
}: QrScannerOptions): void {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const resultCallbackRef = useRef(onResult);
  resultCallbackRef.current = onResult;
  const decodeCallbackRef = useRef(onDecodeMs);
  decodeCallbackRef.current = onDecodeMs;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timer: number | undefined;

    const tick = () => {
      if (cancelled) return;

      const video = videoRef.current;
      if (video && video.readyState >= 2 && video.videoWidth > 0) {
        const canvas = canvasRef.current ?? document.createElement('canvas');
        canvasRef.current = canvas;
        const context = canvas.getContext('2d', { willReadFrequently: true });

        if (context) {
          const scale = Math.min(1, maxWidth / video.videoWidth);
          const width = Math.max(1, Math.round(video.videoWidth * scale));
          const height = Math.max(1, Math.round(video.videoHeight * scale));
          canvas.width = width;
          canvas.height = height;
          context.drawImage(video, 0, 0, width, height);

          const image = context.getImageData(0, 0, width, height);
          const startedAt = performance.now();
          const decoded = jsQR(image.data, width, height, { inversionAttempts: 'attemptBoth' });
          const elapsed = performance.now() - startedAt;

          if (decoded && decoded.data) {
            decodeCallbackRef.current?.(elapsed);
            resultCallbackRef.current(decoded.data);
          }
        }
      }

      timer = window.setTimeout(tick, intervalMs);
    };

    timer = window.setTimeout(tick, intervalMs);

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [enabled, intervalMs, maxWidth, videoRef]);
}
